import ServiceNavbar from '../shared/ServiceNavbar';
import ServiceHero from '../shared/ServiceHero';
import ServiceProcess from '../shared/ServiceProcess';
import ServiceFAQ from '../shared/ServiceFAQ';
import ServiceCTABlock from '../shared/ServiceCTABlock';
import Footer from '../../home/components/Footer';

const processSteps = [
  { icon: 'ri-home-gear-fill', step: 'Step 01', title: 'Home Assessment', description: 'We evaluate your home\'s load requirements, existing panel, fuel options, and ideal generator placement.' },
  { icon: 'ri-calculator-fill', step: 'Step 02', title: 'Equipment Selection', description: 'We recommend the right generator size — whole-home standby or critical circuit — and brand for your needs.' },
  { icon: 'ri-file-list-3-fill', step: 'Step 03', title: 'Permits & Planning', description: 'All required permits pulled, utility notifications sent, and installation plan finalized.' },
  { icon: 'ri-tools-fill', step: 'Step 04', title: 'Professional Installation', description: 'Generator pad, transfer switch, electrical connections, and fuel line coordination by licensed pros.' },
  { icon: 'ri-play-circle-fill', step: 'Step 05', title: 'Test & Commission', description: 'Full load test, automatic transfer verification, and owner walkthrough before we sign off.' },
];

const faqs = [
  { question: 'What size generator do I need for my home?', answer: 'Generator sizing depends on which loads you want to protect. A whole-home standby system typically requires 20–22kW for an average-sized home, while a critical circuit system can often get by with 10–14kW. We perform a load analysis during your assessment to give you a precise recommendation.' },
  { question: 'What\'s the difference between a standby and portable generator?', answer: 'A standby generator is permanently installed, connected to your home\'s electrical system, and starts automatically when power goes out. A portable generator requires manual setup and extension cords (or a transfer switch). For whole-home protection, standby systems are significantly safer and more convenient.' },
  { question: 'What fuel type do standby generators use?', answer: 'Most whole-home standby generators run on natural gas or propane. Natural gas is usually preferred for convenience since it doesn\'t need refilling. We coordinate with your gas utility for proper connection and sizing.' },
  { question: 'Is a transfer switch required?', answer: 'Yes, always. A transfer switch is a code requirement and a safety necessity. It prevents back-feeding electricity into utility lines, which can be fatal to utility workers. We install automatic transfer switches (ATS) as a standard part of every generator installation.' },
  { question: 'How long does generator installation take?', answer: 'Most residential standby generator installations take 1–2 days depending on the scope. This includes pad placement, transfer switch installation, electrical connections, and final testing.' },
];

const GeneratorsPage = () => (
  <div className="min-h-screen bg-[#0A1628]">
    <ServiceNavbar />

    <ServiceHero
      bgImage="https://public.readdy.ai/ai/img_res/deb3a368f87aa520f03ceca948da568b.jpg"
      label="Generator Installation — Kalamazoo"
      headline="Stay Powered."
      headlineAccent="No Matter What."
      subtext="Professional standby and portable generator installation with automatic transfer switches. When the grid goes down, your home stays on — seamlessly, safely, and automatically."
      trustBadges={[
        { icon: 'ri-shield-star-fill', label: 'Licensed & Insured' },
        { icon: 'ri-thunderstorms-fill', label: 'Outage-Ready' },
        { icon: 'ri-settings-3-fill', label: 'Automatic Transfer' },
        { icon: 'ri-award-fill', label: '20+ Years Experience' },
      ]}
    />

    {/* Overview — dark dramatic with amber accent */}
    <section className="relative bg-[#070E1A] py-24 overflow-hidden">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#D97706]/5 blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Main content — 3 cols */}
          <div className="lg:col-span-3">
            <span className="inline-block text-[#D97706] text-[11px] font-semibold uppercase tracking-[3px] mb-5">Why Generators</span>
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-6 leading-tight">
              Michigan Winters Don&apos;t Wait.<br />Neither Should Your Backup Power.
            </h2>
            <p className="text-[#5E7A96] text-[15px] leading-relaxed mb-5">
              Southwest Michigan sees power outages from ice storms, wind events, and severe weather every year. A professionally installed standby generator means your heat, lights, medical equipment, and refrigerator keep running — without you having to do anything.
            </p>
            <p className="text-[#5E7A96] text-[15px] leading-relaxed mb-8">
              DOC Electric installs, maintains, and services all major generator brands including Generac, Kohler, and Briggs &amp; Stratton. Every installation includes a proper automatic transfer switch, code-compliant wiring, and a thorough commissioning test.
            </p>
            <div className="flex flex-col gap-3">
              {[
                'Whole-home and critical circuit standby systems',
                'Automatic Transfer Switch (ATS) installation',
                'Natural gas and propane connections coordinated',
                'Portable generator hookup with interlock kits',
                'Generator maintenance and service',
                'Load analysis for proper sizing',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center rounded flex-shrink-0 bg-[#D97706]/15">
                    <i className="ri-check-line text-[#D97706] text-xs" />
                  </span>
                  <span className="text-[#8BA8C8] text-[14px]">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — 2 cols with image and cards */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            <img
              src="https://readdy.ai/api/search-image?query=automatic%20transfer%20switch%20electrical%20panel%20generator%20backup%20power%20residential%20installation%20professional%20organized%20wiring%20inside%20dark%20navy%20atmosphere%20technical%20precision%20electrical%20engineering&width=600&height=400&seq=gen-overview1&orientation=landscape"
              alt="Generator transfer switch installation"
              className="w-full h-52 object-cover object-top rounded-2xl border border-[#D97706]/20"
            />

            <div className="flex flex-col gap-4">
              {[
                { icon: 'ri-flashlight-fill', label: 'Automatic Activation', desc: 'Starts within seconds of a power outage. No manual intervention required.' },
                { icon: 'ri-home-heart-fill', label: 'Whole-Home Protection', desc: 'Keep every circuit powered or protect only the essentials — your choice.' },
                { icon: 'ri-service-fill', label: 'Maintenance Support', desc: 'We offer ongoing service contracts to keep your generator ready when you need it.' },
              ].map((item) => (
                <div key={item.label} className="bg-[#0A1628] rounded-xl p-5 border border-[#1E3048] hover:border-[#D97706]/30 transition-colors duration-300 flex gap-4">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#D97706]/15 flex-shrink-0">
                    <i className={`${item.icon} text-[#D97706] text-base`} />
                  </span>
                  <div>
                    <div className="text-white text-[13px] font-bold mb-0.5">{item.label}</div>
                    <div className="text-[#4A6A86] text-[12px] leading-snug">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Peace of Mind Banner */}
    <section className="relative py-20 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=cozy%20lit%20house%20neighborhood%20at%20night%20during%20storm%20all%20lights%20on%20while%20surrounding%20homes%20dark%20winter%20ice%20storm%20power%20outage%20warm%20amber%20light%20glowing%20windows%20safety%20home&width=1920&height=400&seq=gen-banner1&orientation=landscape"
          alt="Powered home during outage"
          className="w-full h-full object-cover object-center"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/80 to-transparent" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-lg">
          <h3 className="text-2xl lg:text-3xl font-black text-white mb-4">Peace of Mind, Installed Once.</h3>
          <p className="text-[#8BA8C8] text-[15px] leading-relaxed mb-6">
            Your family&apos;s comfort and safety shouldn&apos;t depend on the grid. One professional installation gives you reliable backup power for years to come.
          </p>
          <a href="#estimate" className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#D97706] hover:bg-[#B86A05] text-white font-bold text-[13px] uppercase tracking-wider rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap">
            Get a Generator Quote
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line" /></span>
          </a>
        </div>
      </div>
    </section>

    <ServiceProcess
      title="Generator Installation Process"
      subtitle="A structured, professionally managed installation from assessment to commissioning."
      steps={processSteps}
      theme="dark"
      accentColor="#D97706"
    />

    {/* Benefits */}
    <section className="bg-[#111C2D] py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <span className="inline-block text-[#D97706] text-[11px] font-semibold uppercase tracking-[3px] mb-4">The Benefits</span>
          <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">What You Get With a Standby Generator</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: 'ri-flashlight-fill', title: 'Automatic Power', desc: 'No action required. Power restores within seconds of any outage, day or night.' },
            { icon: 'ri-temp-cold-fill', title: 'Year-Round Comfort', desc: 'Keep heat, AC, and appliances running through Michigan\'s most severe weather.' },
            { icon: 'ri-heart-pulse-fill', title: 'Medical Equipment Safety', desc: 'Reliable power for oxygen concentrators, CPAP machines, and refrigerated medications.' },
            { icon: 'ri-bank-fill', title: 'Property Protection', desc: 'Prevent frozen pipes, spoiled food, and flood pump failures during extended outages.' },
          ].map((benefit) => (
            <div key={benefit.title} className="bg-[#0A1628] rounded-2xl p-7 border border-[#1E3048] hover:border-[#D97706]/25 hover:-translate-y-1 transition-all duration-300">
              <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#D97706]/12 mb-5">
                <i className={`${benefit.icon} text-[#D97706] text-xl`} />
              </span>
              <h3 className="text-[16px] font-bold text-white mb-2">{benefit.title}</h3>
              <p className="text-[#4A6A86] text-[13px] leading-relaxed">{benefit.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <ServiceFAQ faqs={faqs} theme="dark" />
    <ServiceCTABlock
      servicePreset="Generator Installation"
      headline="Don't Wait for the Next Outage"
      subtext="Get a professional generator assessment and installation quote from DOC Electric. We'll find the right solution for your home and budget."
    />
    <Footer />
  </div>
);

export default GeneratorsPage;
